import { useState, useEffect } from 'react';

interface LocationState {
    latitude: number;
    longitude: number;
    accuracy?: number;
    timestamp?: number;
}

interface UseGeolocationReturn {
    location: LocationState | null;
    error: string | null;
    isLoading: boolean;
}

export const useGeolocation = (): UseGeolocationReturn => {
    const [location, setLocation] = useState<LocationState | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    useEffect(() => {
        if (!navigator.geolocation) {
            setError("Geolocation is not supported by your browser");
            setIsLoading(false);
            return;
        }

        const handleSuccess = (position: GeolocationPosition) => {
            setLocation({
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
                accuracy: position.coords.accuracy,
                timestamp: position.timestamp,
            });
            setIsLoading(false);
            setError(null);
        };

        const handleError = (error: GeolocationPositionError) => {
            setError(error.message);
            setIsLoading(false);
        };

        const options = {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0,
        };

        const watchId = navigator.geolocation.watchPosition(handleSuccess, handleError, options);

        return () => navigator.geolocation.clearWatch(watchId);
    }, []);

    return { location, error, isLoading };
};
